if (!customElements.get('collection-grid-lazy')) {
  class CollectionGridLazy extends HTMLElement {
    constructor() {
      super();
      this.isLoading = false;
      this.observer = null;
    }

    connectedCallback() {
      this.pageSize = Number(this.dataset.pageSize) || 16;
      this.sectionId = this.dataset.sectionId;
      this.grid = this.querySelector('#product-grid');
      this.loadMoreEl = this.querySelector('[data-collection-load-more]');
      this.trigger = this.querySelector('[data-collection-load-more-trigger]');

      if (!this.grid || !this.loadMoreEl) return;

      this.syncFromDataset();
      this.bindEvents();
      this.updateLoadMoreState();
      this.setupIntersectionObserver();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.trigger?.removeEventListener('click', this.onTriggerClick);
    }

    syncFromDataset() {
      this.shopifyPage = Number(this.dataset.currentPage) || 1;
      this.totalPages = Number(this.dataset.totalPages) || 1;
      this.nextPageUrl = this.dataset.nextUrl || '';
    }

    getItems() {
      return [...this.grid.querySelectorAll('.collection-grid__item')];
    }

    getHiddenItems() {
      return this.getItems().filter((item) => item.classList.contains('collection-grid__item--hidden'));
    }

    bindEvents() {
      this.onTriggerClick = () => this.loadMore();
      this.trigger?.addEventListener('click', this.onTriggerClick);
    }

    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window) || !this.loadMoreEl) return;

      this.observer?.disconnect();
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadMore();
          }
        },
        { rootMargin: '120px' }
      );
      this.observer.observe(this.loadMoreEl);
    }

    async loadMore() {
      if (this.isLoading) return;
      this.observer?.unobserve(this.loadMoreEl);

      const hiddenItems = this.getHiddenItems();
      if (hiddenItems.length > 0) {
        this.setLoading(true);
        await this.wait(400);
        hiddenItems.slice(0, this.pageSize).forEach((item) => {
          item.classList.remove('collection-grid__item--hidden');
        });
        this.setLoading(false);
        this.updateLoadMoreState();
        return;
      }

      if (this.nextPageUrl) {
        await this.fetchNextShopifyPage();
      }
    }

    async fetchNextShopifyPage() {
      if (this.isLoading || !this.nextPageUrl) return;

      this.setLoading(true);

      try {
        const nextPage = this.shopifyPage + 1;
        const params = new URLSearchParams(window.location.search);
        params.delete('page');
        params.set('page', String(nextPage));
        const url = `${window.location.pathname}?section_id=${this.sectionId}&${params.toString()}`;

        const response = await fetch(url);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fetchedGrid = doc.querySelector('collection-grid-lazy #product-grid');

        if (!fetchedGrid) return;

        const newItems = [...fetchedGrid.querySelectorAll('.collection-grid__item')];
        newItems.forEach((item) => {
          item.classList.add('collection-grid__item--hidden');
          this.grid.appendChild(item);
        });

        const fetchedLazy = doc.querySelector('collection-grid-lazy');
        if (fetchedLazy) {
          this.dataset.currentPage = fetchedLazy.dataset.currentPage || String(nextPage);
          this.dataset.totalPages = fetchedLazy.dataset.totalPages || this.dataset.totalPages;
          this.dataset.nextUrl = fetchedLazy.dataset.nextUrl || '';
          this.syncFromDataset();
        }

        await this.wait(400);
        const toReveal = this.getHiddenItems().slice(0, this.pageSize);
        toReveal.forEach((item) => item.classList.remove('collection-grid__item--hidden'));
      } catch (error) {
        console.error('Collection grid lazy load:', error);
      } finally {
        this.setLoading(false);
        this.updateLoadMoreState();
      }
    }

    setLoading(loading) {
      this.isLoading = loading;
      this.loadMoreEl?.classList.toggle('collection-load-more--loading', loading);
      this.trigger?.setAttribute('aria-busy', loading ? 'true' : 'false');
      if (this.trigger) {
        this.trigger.disabled = loading;
      }
    }

    updateLoadMoreState() {
      const hasHidden = this.getHiddenItems().length > 0;
      const hasNextPage = Boolean(this.nextPageUrl);
      const show = hasHidden || hasNextPage;

      this.loadMoreEl?.classList.toggle('hidden', !show);
      this.classList.toggle('collection-grid-lazy--complete', !show);

      if (!show) {
        this.observer?.disconnect();
      } else {
        this.setupIntersectionObserver();
      }
    }

    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }
  }

  customElements.define('collection-grid-lazy', CollectionGridLazy);
}
